import { createFileRoute } from "@tanstack/react-router";

import { GuidePage, type GuideFaq, type GuideSection } from "@/components/seo/GuidePage";
import { guidePageHead } from "@/lib/guide-page-seo";

const PATH = "/guides/dubai-property-ad-permit";
const HEADLINE = "The Dubai property ad permit and its QR code, on Meta";
const TITLE = "Trakheesi Permit and QR Code for Dubai Property Ads on Meta | Adkiro";
const DESCRIPTION =
  "A property advertisement in Dubai needs a permit from the Land Department's Trakheesi system and, since 24 April 2023, its Madmoun QR code. What that means for Facebook and Instagram campaigns.";

const KEY_POINTS = [
  "A property advertisement in Dubai needs a permit, requested through the Dubai Land Department's Trakheesi system. The permit types it lists include electronic advertisements and real estate promotion platforms, as well as print, outdoor and SMS.",
  "Since 24 April 2023 every permit carries a Madmoun QR code. Scanning it shows who is advertising, the property's details and whether it is still available.",
  "A standard permit costs AED 1,000 plus a AED 20 knowledge and innovation fee, and a project launch permit AED 5,000. The Land Department quotes one working day to issue it.",
  "A broker applies with the marketing contract signed by the owner, so the permit comes before the campaign and cannot be added on the day it is due to start.",
  "For Facebook and Instagram the working rule is a permit for each advertised listing or project, its QR code on the creative, and the ad switched off when the listing is gone.",
];

const SECTIONS: GuideSection[] = [
  {
    heading: "What the rule is",
    body: [
      "On 18 April 2023 the Dubai Land Department announced that its Real Estate Regulatory Agency had issued a circular introducing Madmoun, a QR code attached to each real estate advertisement permit. It became a requirement on 24 April 2023.",
      "The code is activated through Trakheesi, the Land Department's permit system. Anyone who scans it sees the details of the advertisement, the company behind it and the state of the property, including whether it has already been sold or let. A buyer can check in a few seconds that a listing is real and still on the market.",
      "The permit itself was not new in 2023. What changed is that it became visible: the advertisement now carries the proof that it was allowed.",
    ],
  },
  {
    heading: "Which advertisements need a permit",
    body: [
      "The Land Department's permit service lists fourteen kinds of advertisement and event. Alongside newspaper, outdoor, vehicle, printed and billboard advertisements it names electronic advertisements, SMS advertisements, classified advertisements, promotional campaigns and real estate promotion platforms.",
      "The announcement of the QR code speaks of print and audiovisual advertisements and does not mention Facebook or Instagram by name. The cautious reading is that an advertisement for a specific Dubai property on social media is an electronic advertisement, and needs its permit like any other.",
      "Where the position of the code on a social media ad is unclear, the regulator is the authority to ask. An agency's opinion, including this one, is not a ruling.",
    ],
    points: [
      "A standard permit: AED 1,000, plus a AED 20 knowledge and innovation fee.",
      "A project launch event permit: AED 5,000.",
      "Requested through Trakheesi on the Land Department's website; one working day by its own estimate.",
      "A broker supplies the marketing contract signed with the property's owner.",
    ],
  },
  {
    heading: "What it changes in a Meta campaign",
    body: [
      "A Meta campaign is usually planned around audiences and budgets. For Dubai property it has to be planned around permits first, because the advertised listing or project needs its permit before the ad goes live.",
      "That changes the order of work. The permit is requested when the listing is signed, the creative is built with the QR code in it, and someone keeps a simple register of which ad carries which permit. When a property sells, its code says so to anyone who scans it, and an ad still running for it is advertising something that is no longer for sale.",
      "It also limits a habit that is common on Meta: one generic ad for many properties. A carousel of ten listings raises the question of ten permits. A campaign built around one property and one idea fits the rule without effort.",
    ],
    points: [
      "Request the permit when the listing is signed, not when the campaign is due.",
      "Put the QR code on the creative, clear enough to scan.",
      "Keep a register: ad, listing, permit number, start date.",
      "Switch the ad off when the listing is sold or let.",
    ],
  },
  {
    heading: "What it costs to ignore",
    body: [
      "On 8 February 2024 the Land Department announced that 30 real estate companies had each been fined AED 50,000 for failing to comply with the terms set for real estate advertisements. Its statement told companies to obtain advertising licences, to give accurate information and to include the QR code, and it told the public to avoid advertisements without a permit and a code.",
      "The second cost is quieter. In a market where buyers have been told to scan before they enquire, an ad that cannot be verified loses the careful buyer first, and the careful buyer is the one a sales team wants.",
    ],
  },
  {
    heading: "What this guide is, and is not",
    body: [
      "This is a working guide for marketing teams, written from the Land Department's own published pages, which are listed below. It is not legal advice. Rules and fees change, and the regulator's current guidance takes precedence over anything written here.",
    ],
  },
];

const FAQS: GuideFaq[] = [
  {
    q: "Do Facebook and Instagram ads for a Dubai property need a Trakheesi permit?",
    a: "An advertisement for a property in Dubai needs a permit, requested through the Dubai Land Department's Trakheesi system, and the permit types it lists include electronic advertisements and real estate promotion platforms. The 2023 announcement of the Madmoun QR code names print and audiovisual advertisements and does not mention social media by name, so the cautious practice is to treat a Facebook or Instagram ad for a specific listing as needing its permit, and to ask the regulator where there is doubt.",
  },
  {
    q: "What is the Madmoun QR code?",
    a: "Madmoun is a QR code attached to each real estate advertisement permit in Dubai, required since 24 April 2023. It is activated through the Land Department's Trakheesi system. Scanning it shows the details of the advertisement, the company behind it and the state of the property, including whether it has been sold or let.",
  },
  {
    q: "How much does a property advertising permit cost in Dubai?",
    a: "The Dubai Land Department's service page lists AED 1,000 for a standard advertisement permit, plus a AED 20 knowledge and innovation fee, and AED 5,000 for a project launch event permit. It is requested through Trakheesi and the stated processing time is one working day. Check the Land Department's page for the current figures before budgeting.",
  },
  {
    q: "What happens if a property is advertised in Dubai without a permit?",
    a: "The regulator fines companies. On 8 February 2024 the Dubai Land Department announced that 30 real estate companies had each been fined AED 50,000 for failing to comply with the terms set for real estate advertisements, and it reminded companies to obtain advertising licences and to include the QR code.",
  },
  {
    q: "Who applies for the permit: the brokerage or its marketing agency?",
    a: "The company advertising the property applies, through Trakheesi; the Land Department's service page lists a broker's marketing contract with the owner among the documents. An outside agency builds the campaign around the permit its client holds, and should ask for the permit number before any ad for that listing is built.",
  },
];

export const Route = createFileRoute("/guides/dubai-property-ad-permit")({
  head: () =>
    guidePageHead({
      path: PATH,
      title: TITLE,
      description: DESCRIPTION,
      headline: HEADLINE,
      published: "2026-10-03",
      modified: "2026-10-03",
      faqs: FAQS,
    }),
  component: Page,
});

function Page() {
  return (
    <GuidePage
      eyebrow="Dubai property"
      headline={HEADLINE}
      standfirst="Advertising a property in Dubai needs a permit, and since April 2023 the permit's QR code has to appear on the advertisement. A Facebook or Instagram campaign for a Dubai listing should be planned around that permit, not around the audience."
      keyPoints={KEY_POINTS}
      sections={SECTIONS}
      faqs={FAQS}
      updated="3 October 2026"
      readingMinutes={6}
      sources={[
        {
          href: "https://dubailand.gov.ae/en/news-media/dubai-land-department-provides-madmoun-service-to-verify-validity-of-real-estate-ads-via-qr-codes",
          label: "Dubai Land Department: the Madmoun QR code, 18 April 2023",
        },
        {
          href: "https://dubailand.gov.ae/en/eservices/real-estate-ad-permit/",
          label: "Dubai Land Department: real estate advertisement permit service",
        },
        {
          href: "https://dubailand.gov.ae/en/news-media/dld-fines-30-real-estate-companies-for-failing-to-comply-with-the-terms-and-conditions-specified-in-real-estate-advertisements",
          label: "Dubai Land Department: 30 companies fined, 8 February 2024",
        },
      ]}
      related={[
        {
          href: "/guides/meta-housing-rules-dubai-property",
          label: "Meta's housing rules, when Dubai property is advertised abroad",
        },
        {
          href: "/real-estate-lead-generation-meta-ads",
          label: "Meta ads for property agencies and developers",
        },
        { href: "/meta-ads-audit", label: "The senior Meta Ads account review" },
      ]}
    />
  );
}
